import type {ThemePreference} from "@project/design-system-core";

import {Kbd, ToggleButton, ToggleButtonGroup, Tooltip} from "@heroui/react";
import {THEME_REGISTRY} from "@project/design-system-core";
import {MoonIcon, SunIcon, SystemIcon} from "@project/icons-web";
import {useEffect} from "react";
import {tv} from "tailwind-variants";

import {useTheme} from "../hooks/use-theme";

type Option = {value: ThemePreference; label: string; icon: React.ReactNode};

const OPTIONS: Option[] = [
  {label: "System", value: "system", icon: <SystemIcon className="size-4" />},
  ...Object.values(THEME_REGISTRY).map((t) => ({
    icon: t.id === "light" ? <SunIcon className="size-4" /> : <MoonIcon className="size-4" />,
    label: t.label,
    value: t.id,
  })),
];

/** Tailwind Variants for theme switcher styling */
const themeSwitcherVariants = tv({
  base: "border-default/30 bg-surface inline-flex items-center gap-1 rounded-lg border p-1",
  variants: {},
});

const toggleButtonVariants = tv({
  base: "inline-flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors",
  variants: {},
});

/**
 * Self-contained theme picker with icon support.
 * Reads current preference via useTheme() and allows switching between
 * 'system', 'light', and 'dark' (plus any future custom themes in the registry).
 * Keyboard shortcut: Ctrl+S (or Cmd+S on Mac) to cycle through themes.
 * No props required.
 */
export function ThemeSwitcher() {
  const {preference, setPreference} = useTheme();

  // Handle Ctrl+S / Cmd+S keyboard shortcut to cycle through themes
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+S (Windows/Linux) or Cmd+S (Mac)
      const isCtrlOrCmd = event.ctrlKey || event.metaKey;

      if (isCtrlOrCmd && event.key === "s") {
        event.preventDefault();

        // Find current index and cycle to next theme
        const currentIndex = OPTIONS.findIndex((opt) => opt.value === preference);
        const nextIndex = (currentIndex + 1) % OPTIONS.length;
        const nextTheme = OPTIONS[nextIndex].value;

        setPreference(nextTheme);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [preference, setPreference]);

  return (
    <Tooltip delay={200}>
      <Tooltip.Trigger>
        <ToggleButtonGroup
          aria-label="Theme preference"
          selectedKeys={[preference]}
          selectionMode="single"
          disallowEmptySelection
          onSelectionChange={(keys) => {
            const selected = Array.from(keys)[0] as ThemePreference;

            setPreference(selected);
          }}
          className={themeSwitcherVariants()}
          data-theme-switcher=""
        >
          {OPTIONS.map(({icon, label, value}, index) => (
            <ToggleButton key={value} id={value} className={toggleButtonVariants()}>
              {index > 0 && <ToggleButtonGroup.Separator />}
              {icon}
              <span>{label}</span>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Tooltip.Trigger>
      <Tooltip.Content placement="bottom">
        <div className="flex items-center justify-center gap-2">
          <span>Switch mode</span>
          <Kbd>
            <Kbd.Abbr keyValue="command" />
            <Kbd.Content>S</Kbd.Content>
          </Kbd>
        </div>
      </Tooltip.Content>
    </Tooltip>
  );
}
